/** Form and control atoms. Copy always arrives as a resolved string. */
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { forwardRef, useId } from 'react'

type Variant = 'primary' | 'secondary' | 'quiet' | 'destructive'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  block?: boolean
  /**
   * The request this button fired is still in flight. flows.json's `retrying`
   * state asks for a spinner IN PLACE OF the label and nothing else moving, so
   * the button keeps its measured width and the layout does not twitch.
   */
  busy?: boolean
  /** Keep keyed progress copy visible beside the spinner (provider controls). */
  showBusyLabel?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', block, busy, showBusyLabel, className, type = 'button', children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={['btn', `btn-${variant}`, block ? 'btn-block' : '', className ?? ''].join(' ').trim()}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      {...rest}
    >
      {busy && showBusyLabel ? (
        <>
          <span className="spinner spinner-inline" aria-hidden="true" />
          <span>{children}</span>
        </>
      ) : busy ? (
        <>
          <span className="spinner" aria-hidden="true" />
          {/* The label leaves the flow but keeps reserving its width. */}
          <span className="btn-label-placeholder">{children}</span>
        </>
      ) : (
        children
      )}
    </button>
  )
})

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'neutral' | 'positive' | 'caution' | 'danger' | 'info'
}) {
  return <span className={`badge${tone === 'neutral' ? '' : ` badge-${tone}`}`}>{children}</span>
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string
  error?: string | null
  hint?: string
}

export function TextField({ label, error, hint, id, ...rest }: TextFieldProps) {
  const generated = useId()
  const fieldId = id ?? generated
  return (
    <div className="field">
      <label className="label" htmlFor={fieldId}>
        {label}
      </label>
      <input
        id={fieldId}
        className="input"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        {...rest}
      />
      {hint ? <span className="caption">{hint}</span> : null}
      {error ? (
        <span className="inline-error" id={`${fieldId}-error`} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  )
}

export function InlineError({ children }: { children: ReactNode }) {
  return (
    <p className="inline-error" role="alert">
      {children}
    </p>
  )
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      className="toggle"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  )
}

export interface SegmentOption<T extends string> {
  value: T
  label: string
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: SegmentOption<T>[]
  value: T
  onChange: (next: T) => void
  label: string
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export function Chip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button type="button" className="chip" aria-pressed={active} onClick={onClick}>
      {label}
    </button>
  )
}

export function StepList({ steps }: { steps: string[] }) {
  return (
    <ol className="step-list">
      {steps.map((step, i) => (
        <li key={i}>{step}</li>
      ))}
    </ol>
  )
}

export function Banner({
  tone = 'info',
  children,
  action,
}: {
  tone?: 'info' | 'caution' | 'danger'
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div className={`banner banner-${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <span>{children}</span>
      {action ? <span className="banner-actions">{action}</span> : null}
    </div>
  )
}
